// src/components/Control/Operator/StatusStrip.tsx
// Строка статуса: связь, Zenoh, батарея, режим, скорость, люди, лидар, камеры,
// препятствия. Нажатие на пункт — объяснение и переход в нужный раздел.
// У Pi без шасси нет скорости, лидара и препятствий (и камеры 2, если она не прислала статус).
import React from 'react';
import { useUiActions, SettingsSection } from '../robot/RobotContext';
import { Dot, usePopover } from './ui';
import { RobotStatus, Level, ago, useRobotStatus } from './status';
import { ModeMenu } from './Header';

interface Item {
  id: string;
  level: Level;
  label: string;
  short?: string;            // для mobile
  title: string;
  lines: string[];
  section?: SettingsSection;
  sectionLabel?: string;
}

function items(s: RobotStatus): Item[] {
  const st = s.status;
  const out: Item[] = [];
  out.push({
    id: 'robot', level: s.conn.level, label: `Робот: ${s.conn.text}`, short: 'Робот', title: 'Связь с роботом',
    lines: [s.conn.detail, `Последний контакт: ${s.statusAt ? new Date(s.statusAt).toLocaleTimeString() : '—'}`,
      ...(st?.firmware ? [`Прошивка контроллера: ${st.firmware}`] : [])],
    section: 'diagnostics', sectionLabel: 'Диагностика',
  });
  const zl: Level = s.zstate === 'connected' ? 'ok' : s.zstate === 'idle' ? 'off' : 'warn';
  out.push({
    id: 'zenoh', level: zl, label: 'Zenoh', title: 'Zenoh',
    lines: [{ connected: 'Подключено', connecting: 'Подключение…', reconnecting: 'Переподключение…', idle: 'Отключено вручную' }[s.zstate]],
    section: 'zenoh', sectionLabel: 'Настройки Zenoh',
  });
  if (s.asleep) {
    out.push({
      id: 'sleep', level: 'warn', label: '💤 Робот спит', short: '💤 Спит', title: 'Режим сна',
      lines: [s.power?.reason === 'no_clients' ? 'Уснул: никого не было на связи.' : 'Усыплён с сайта.',
        'Камеры, распознавание и прослушивание выключены; связь и режим няни работают.'],
      section: 'power', sectionLabel: 'Разбудить',
    });
  }
  const block = st?.drive_block;
  if (s.chassis && block) {
    out.push({
      id: 'drive_block', level: 'warn', label: `⛔ Вперёд не едет: ${block === 'lidar' ? 'лидар' : 'камера 2'}`,
      short: '⛔ Вперёд', title: 'Команда «вперёд» не выполняется',
      lines: [block === 'lidar'
        ? 'Лидар видит препятствие в коридоре шириной с корпус. Назад и повороты работают.'
        : 'Камера 2 видит препятствие по центру. Назад и повороты работают.',
        block === 'lidar' ? 'Выключить: «Управление» → «Не ехать вперёд в препятствие по лидару».'
          : 'Выключить: «Камеры» → «Стоп перед препятствием».'],
      section: block === 'lidar' ? 'navigation' : 'cameras', sectionLabel: block === 'lidar' ? 'Управление' : 'Камеры',
    });
  }
  const users = Object.entries(s.clients.users).map(([u, n]) => (n > 1 ? `${u} ×${n}` : u)).join(', ');
  out.push({
    id: 'clients', level: 'off', label: `👥 Клиентов: ${s.clients.count}`, short: `👥 ${s.clients.count}`,
    title: 'Кто на связи с роботом',
    lines: [s.clients.count ? `Открытых страниц робота: ${s.clients.count} (${users})` : 'Нет открытых страниц робота',
      'Считаются все вкладки с этим роботом, ваша тоже.'],
    section: 'power', sectionLabel: 'Питание и сон',
  });
  if (s.chassis) out.push({
    id: 'speed', level: 'off', label: `Скорость ${s.speed != null ? `${s.speed.toFixed(2)} м/с` : '—'}`,
    short: s.speed != null ? `${s.speed.toFixed(2)} м/с` : '— м/с',
    title: 'Скорость', lines: ['Скорость робота по одометрии.'],
    section: 'navigation', sectionLabel: 'Управление',
  });
  const names = s.people.map(p => p.known?.name).filter(Boolean);
  out.push({
    id: 'people', level: s.people.length ? 'ok' : 'off', label: `Люди: ${s.people.length}`, short: `👤 ${s.people.length}`,
    title: 'Люди в кадре камеры 1',
    lines: [s.people.length ? `В кадре: ${s.people.length}${names.length ? ` (${names.join(', ')})` : ''}` : 'В кадре никого нет'],
    section: 'people', sectionLabel: 'Люди и распознавание',
  });
  if (s.chassis) out.push({
    id: 'lidar', level: s.lidarOk ? 'ok' : s.asleep ? 'off' : 'error', label: s.lidarOk || s.asleep ? 'LiDAR' : 'LiDAR офлайн',
    title: 'LiDAR',
    lines: [s.lidarOk ? `На связи · ${s.scanHz.toFixed(1)} скан/с` : s.asleep ? 'Не передаётся: робот спит' : 'Нет данных лидара',
      `Последний ответ: ${s.scanAt ? new Date(s.scanAt).toLocaleTimeString() : '—'}`],
    section: 'lidar', sectionLabel: 'LiDAR',
  });
  const c1: Level = s.cam1 == null || s.asleep ? 'off' : s.cam1 ? 'ok' : 'error';
  out.push({
    id: 'cam1', level: c1, label: c1 === 'error' ? 'Камера 1 офлайн' : 'Камера 1', short: 'Кам 1', title: 'Камера 1 (Pi)',
    lines: [s.cam1 == null ? 'Нет данных от робота' : s.asleep ? 'Выключена: робот спит' : s.cam1 ? 'Кадры идут'
      : 'Кадров нет (передача выключена или камера не работает)'],
    section: 'cameras', sectionLabel: 'Камеры',
  });
  const c2: Level = !s.cam2 ? 'off' : s.cam2.online ? 'ok' : 'error';
  if (s.chassis || s.cam2) out.push({
    id: 'cam2', level: c2, label: c2 === 'error' ? 'Камера 2 офлайн' : 'Камера 2', short: 'Кам 2', title: 'Камера 2 (ESP32-S3)',
    lines: [!s.cam2 ? 'Нет данных' : s.cam2.online
      ? `На связи, ${s.cam2.fps ?? 0} к/с${s.cam2.rssi != null ? `, Wi-Fi ${s.cam2.rssi} дБм` : ''}${s.cam2.firmware ? `, прошивка ${s.cam2.firmware}` : ''}`
      : 'Не на связи'],
    section: 'cameras', sectionLabel: 'Камеры',
  });
  const stopOn = !!st?.settings?.stop_on_obstacle;
  if (s.chassis && (s.obstacle || stopOn)) {
    const ob = s.obstacle;
    out.push({
      id: 'obstacle', level: ob?.obstacle ? 'warn' : ob ? 'ok' : 'off',
      label: ob ? (ob.obstacle ? 'Препятствие впереди' : 'Впереди свободно') : 'Препятствия: нет данных',
      short: ob?.obstacle ? '⚠ Препятствие' : 'Путь',
      title: 'Препятствия по камере 2',
      lines: [ob ? `${ob.obstacle ? 'Препятствие впереди' : 'Впереди свободно'} · детекция на ${ob.source === 'rpi' ? 'Pi' : 'ESP'}` : 'Детекция выключена или камера 2 не на связи',
        stopOn ? 'Стоп перед препятствием: включён (вперёд не едет)' : 'Стоп перед препятствием: выключен'],
      section: 'cameras', sectionLabel: 'Камеры',
    });
  }
  if (s.nanny) {
    out.push({ id: 'nanny', level: 'ok', label: 'Няня включена', short: 'Няня', title: 'Режим няни', lines: ['Робот слушает и сообщает о звуках.'], section: 'nanny', sectionLabel: 'Настройки няни' });
  }
  return out;
}

const StatusItem: React.FC<{ item: Item; compact?: boolean }> = ({ item, compact }) => {
  const pop = usePopover();
  const { openSettings } = useUiActions();
  return (
    <div className="op-pop-wrap" ref={pop.ref}>
      <button type="button" className={`op-status-item ${item.level}`} onClick={() => pop.setOpen(!pop.open)}
              aria-expanded={pop.open} aria-label={`${item.title}: ${item.lines[0]}`}>
        <Dot level={item.level} />
        <span>{compact ? item.short ?? item.label : item.label}</span>
      </button>
      {pop.open && (
        <div className="op-popover up" role="dialog" aria-label={item.title}>
          <div className="op-pop-title">{item.title}</div>
          {item.lines.map((l, i) => <p key={i} className={i ? 'op-muted' : ''}>{l}</p>)}
          {item.section && (
            <button type="button" className="op-btn" onClick={() => { pop.setOpen(false); openSettings(item.section!); }}>
              {item.sectionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const StatusStrip: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const s = useRobotStatus();
  return (
    <div className={`op-status ${compact ? 'compact' : ''}`} role="status" aria-label="Состояние робота">
      {compact && <ModeMenu s={s} compact />}
      {items(s).filter(i => !compact || i.id !== 'zenoh' || i.level !== 'ok').map(i => <StatusItem key={i.id} item={i} compact={compact} />)}
      {!compact && <span className="op-status-time">Последний контакт: {s.statusAt ? ago(s.statusAt) : '—'}</span>}
    </div>
  );
};

export default StatusStrip;
